<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import VuePdfEmbed from 'vue-pdf-embed'
import ability from '@/plugins/casl/ability'
import {
  $crApprovalFileAttachmentAnnotationCreate,
  $crApprovalFileAttachmentAnnotationDelete,
  $crApprovalFileAttachmentAnnotationUpdate,
  $crFileAttachmentAnnotationCreate,
  $crFileAttachmentAnnotationDelete,
  $crFileAttachmentAnnotationUpdate,
  $crSubtaskFileAttachmentAnnotationCreate,
  $crSubtaskFileAttachmentAnnotationDelete,
  $crSubtaskFileAttachmentAnnotationUpdate
} from '@/api/changeRequests'
import { isImageFile } from '@/helpers/utils/checkFileUrlType/isImageFile'
import { getFileExtension } from '@/helpers/utils/checkFileUrlType/getFileExtension'

interface IProps {
  fileAttachment: any,
  annotations: any[],
  fileCurrentPage: number,
  fileLoading: boolean,
  pdfScale: number,
  headerHeight: number,
  crItemType: 'cr' | 'crApproval' | 'crSubtask',
}

const props = withDefaults(defineProps<IProps>(), {
  fileAttachment: null,
  annotations: () => ([]),
  fileCurrentPage: 1,
  fileLoading: true,
  pdfScale: 100,
  headerHeight: 48.5,
  crItemType: 'cr',
})
interface Emit {
  (e: 'update:annotations', value: any): void
  (e: 'update:fileCurrentPage', value: number): void
  (e: 'update:fileLoading', value: boolean): void
  (e: 'getAnnotations'): void
  (e: 'onSetHover', value: any): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()

interface MapPosition { left: number; top: number; x: number; y: number; }

const artworkFilePdf = ref<InstanceType<typeof VuePdfEmbed> | null>(null)
const artworkFilePageCount = ref<number>(1)
const isMoving = ref<boolean>(false)
const artworkFileMapPos = ref<MapPosition>({ left: 0, top: 0, x: 0, y: 0 })

const isCanAddAnnotation = computed<boolean>(() => {
  // return ability.can('add', 'crfileannot') && props.cr.is_active
  return props.fileAttachment?.has_ordinary_access === true
})

const isCanChangeAnnotation = computed<boolean>(() => {
  // return ability.can('change', 'crfileannot') && props.cr.is_active
  return props.fileAttachment?.has_ordinary_access === true
})

const isCanDeleteAnnotation = computed<boolean>(() => {
  // return ability.can('delete', 'crfileannot') && props.cr.is_active
  return props.fileAttachment?.has_ordinary_access === true
})

const isCanResolveAnnotation = computed<boolean>(() => {
  // return ability.can('resolve', 'crfileannot') && props.cr.is_active
  return props.fileAttachment?.has_ordinary_access === true
})

const artworkFileUrl = computed(() => {
  return props.fileAttachment?.file || null
})

const isImage = computed<boolean>(() => {
  const url = artworkFileUrl.value
  if (!url) return false
  return isImageFile(url)
})

const isPdf = computed<boolean>(() => {
  return getFileExtension(artworkFileUrl.value) === 'pdf'
})

const isFileDisplayable = computed<boolean>(() => {
  return isImage.value || isPdf.value
})

const isShownAnyAnnotationMenu = computed<boolean>(() => {
  return props.annotations.some(
    annot => annot.page === props.fileCurrentPage && !!annot.isShownAnnotationMenu
  )
})

watch(() => artworkFileUrl.value, (newUrl) => {
  if (!newUrl) return

  if (isFileDisplayable.value) {
    emit('update:fileLoading', true)
  }

  if (isImage.value) {
    // For image files, ensure page count is 1 and current page is set to 1
    artworkFilePageCount.value = 1
    emit('update:fileCurrentPage', 1)
  }

  if (!isFileDisplayable.value) {
    artworkFilePageCount.value = 1
    emit('update:fileCurrentPage', 1)
    emit('update:fileLoading', false)
  }
}, { immediate: true })

const addAnnotation = (newAnnotation: any) => {
  const updatedAnnotations = [...props.annotations, newAnnotation]
  emit('update:annotations', updatedAnnotations)
}

const hideAnnotationMenus = () => {
  emit('update:annotations', props.annotations.map(i => ({ ...i, isShownAnnotationMenu: false })))
}

const createAnnotation = async (event: MouseEvent) => {
  if (!isCanAddAnnotation.value || !props?.fileAttachment?.id || !isFileDisplayable.value) return

  const target = event.target as HTMLElement
  // If any annotation menu is open, close them instead of creating a new one
  if (isShownAnyAnnotationMenu.value) {
    hideAnnotationMenus()
    return
  }

  // Only proceed if clicking on the PDF text layer (for PDF) or the image element (for images)
  if (
    (!isImage.value && target.classList.contains('textLayer')) ||
    (isImage.value && target.id === 'artwork-file-image')
  ) {
    // Calculate percentages based on click position
    const offsetX = event.offsetX
    const offsetY = event.offsetY
    const width = target.clientWidth || 1
    const height = target.clientHeight || 1
    const leftPerc = ((offsetX - 12) / width) * 100
    const topPerc = ((offsetY - 12) / height) * 100

    const payload = {
      file_attachment: props.fileAttachment.id,
      top: topPerc,
      left: leftPerc,
      page: props.fileCurrentPage
    }

    try {
      let res
      switch (props.crItemType) {
        case 'crApproval':
          res = await $crApprovalFileAttachmentAnnotationCreate(payload)
          break
        case 'crSubtask':
          res = await $crSubtaskFileAttachmentAnnotationCreate(payload)
          break
        default:
          res = await $crFileAttachmentAnnotationCreate(payload)
          break
      }
      addAnnotation(res.data)
    } catch (e) {
      console.log(e)
    }
  }
}

const updateAnnotation = async (annotation: any) => {
  if (!isCanChangeAnnotation.value) return
  try {
    switch (props.crItemType) {
      case 'crApproval':
        await $crApprovalFileAttachmentAnnotationUpdate(annotation.id, annotation)
        break
      case 'crSubtask':
        await $crSubtaskFileAttachmentAnnotationUpdate(annotation.id, annotation)
        break
      default:
        await $crFileAttachmentAnnotationUpdate(annotation.id, annotation)
        break
    }
    hideAnnotationMenus()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Annotation saved successfully!'
    })
  } catch (e) {
    console.log(e)
  }
}

const deleteAnnotation = async (id: number) => {
  if (!isCanDeleteAnnotation.value) return
  try {
    switch (props.crItemType) {
      case 'crApproval':
        await $crApprovalFileAttachmentAnnotationDelete(id)
        break
      case 'crSubtask':
        await $crSubtaskFileAttachmentAnnotationDelete(id)
        break
      default:
        await $crFileAttachmentAnnotationDelete(id)
        break
    }
    emit('getAnnotations')
    hideAnnotationMenus()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Annotation deleted successfully!'
    })
  } catch (e) {
    console.log(e)
  }
}

const onMoveMapStart = (e: MouseEvent, mapPos: MapPosition, elementId: string) => {
  // On mouse left button (primary) - move PDF/Image
  if (e.button === 0) {
    const el = document.getElementById(elementId) as HTMLElement
    isMoving.value = true
    mapPos.left = el.scrollLeft
    mapPos.top = el.scrollTop
    mapPos.x = e.clientX
    mapPos.y = e.clientY
  }
}

const onMoveMap = (e: MouseEvent, isCenter: boolean, mapPos: MapPosition, elementId: string) => {
  if (isCenter) {
    setTimeout(() => {
      const containerIds = ['artwork-file-pdf-container-id']
      containerIds.forEach((containerId: string) => {
        const container = document.getElementById(containerId) as HTMLElement
        container.scrollTop = container.scrollHeight / 2 - container.clientHeight / 2
        container.scrollLeft = container.scrollWidth / 2 - container.clientWidth / 2
      })
    }, 0)
  } else {
    const el = document.getElementById(elementId) as HTMLElement
    const dx = e.clientX - mapPos.x
    const dy = e.clientY - mapPos.y

    el.scrollTop = mapPos.top - dy
    el.scrollLeft = mapPos.left - dx
  }
}
</script>

<template>
  <VRow
    ref="artworksBlock"
    class="artwork-row mx-0"
    :style="{ '--header-height': `${headerHeight}px` }"
  >
    <VCol
      class="position-relative py-0 px-1"
      :class="{ 'min-height-200': fileLoading }"
    >
      <div
        v-if="artworkFileUrl"
        style="margin: auto; max-height: inherit"
      >
        <VOverlay
          :model-value="fileLoading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </VOverlay>
        <!-- Pagination for PDF pages (disabled for images since pageCount = 1) -->
        <div v-if="isFileDisplayable" class="text-center">
          <VBtn
            size="sm"
            class="p-0 rounded-circle"
            pill
            color="light"
            :disabled="fileCurrentPage === 1"
            @click="emit('update:fileCurrentPage', fileCurrentPage - 1)"
          >
            <VIcon icon="mdi-arrow-left-bold-circle-outline" />
          </VBtn>
          <span>
            {{ fileCurrentPage }}
          </span>
          <VBtn
            size="sm"
            class="p-0 rounded-circle"
            pill
            color="light"
            :disabled="fileCurrentPage === artworkFilePageCount"
            @click="emit('update:fileCurrentPage', fileCurrentPage + 1)"
          >
            <VIcon icon="mdi-arrow-right-bold-circle-outline" />
          </VBtn>
        </div>

        <div
          v-if="isFileDisplayable"
          id="artwork-file-pdf-container-id"
          class="artwork-file-pdf-container"
          @mousedown="onMoveMapStart($event, artworkFileMapPos, 'artwork-file-pdf-container-id')"
          v-on="{ mousemove: (event) => { if (isMoving) onMoveMap(event, false, artworkFileMapPos, 'artwork-file-pdf-container-id') } }"
          @mouseup="isMoving = false"
          @mouseout="isMoving = false"
        >
          <div
            class="artwork-file-pdf-wrapper overflow-hidden"
            :style="{ width: `${pdfScale}%` }"
            @click.right.prevent="(event) => { createAnnotation(event) }"
          >
            <!-- PDF display -->
            <VuePdfEmbed
              v-if="isPdf"
              ref="artworkFilePdf"
              id="artwork-file-pdf"
              textLayer
              :source="artworkFileUrl"
              :page="fileCurrentPage"
              @rendered="emit('update:fileLoading', false); artworkFilePageCount = artworkFilePdf.pageCount"
            />
            <!-- Image display -->
            <img
              v-else
              id="artwork-file-image"
              :src="artworkFileUrl"
              alt="Artwork File"
              @load="emit('update:fileLoading', false)"
              style="width: 100%; display: block;"
              draggable="false"
            />
            <!-- Annotations overlay -->
            <div
              v-for="(annotation, key) in annotations.filter(annot => annot.page === fileCurrentPage)"
              :id="`annotation${key}`"
              :key="key"
              class="pdfAnnotation"
              :class="{ 'hovered': annotation.hover, 'd-none': fileLoading }"
              :style="{ left: annotation.left + '%', top: annotation.top + '%' }"
              annot="true"
              @mouseover="emit('onSetHover', { annotation: annotation, status: true })"
              @mouseout="emit('onSetHover', { annotation: annotation, status: false })"
            >
              <span annot="true">
                {{ annotation.index }}
              </span>
              <VMenu
                v-model="annotation.isShownAnnotationMenu"
                :id="'pop' + annotation.id"
                :activator="`#annotation${key}`"
                :open-on-click="true"
                :open-on-hover="false"
                :close-on-content-click="false"
                location="top center"
                offset="5"
              >
                <VCard>
                  <VCardText
                    class="border rounded-lg pa-3"
                  >
                    <VTextarea
                      v-model="annotation.comment"
                      placeholder="Enter comments..."
                      class="mb-1"
                      rows="3"
                      :disabled="annotation.is_resolved || !isCanChangeAnnotation"
                    />
                    <div
                      v-if="!annotation.is_resolved"
                      class="mt-1 d-flex"
                    >
                      <div
                        v-if="isCanChangeAnnotation"
                        class="save-btn"
                      >
                        <VBtn
                          color="success"
                          :disabled="!annotation.comment"
                          @click="updateAnnotation(annotation)"
                        >
                          Save
                        </VBtn>
                      </div>
                      <VTooltip
                        v-if="!annotation.comment"
                        activator=".save-btn"
                        location="top center"
                        offset="5"
                        class="default-tooltip"
                      >
                        <span>
                          Please, add comment
                        </span>
                      </VTooltip>
                      <div
                        v-if="isCanResolveAnnotation"
                        class="set-resolved-btn"
                      >
                        <VBtn
                          class="ml-1"
                          color="info"
                          :disabled="!annotation.comment"
                          @click="annotation.is_resolved=true; updateAnnotation(annotation)"
                        >
                          Set Resolved
                        </VBtn>
                      </div>
                      <VTooltip
                        v-if="!annotation.comment"
                        activator=".set-resolved-btn"
                        location="top center"
                        offset="5"
                        class="default-tooltip"
                      >
                        <span>
                          Please, add comment
                        </span>
                      </VTooltip>
                      <VBtn
                        v-if="isCanDeleteAnnotation"
                        class="ml-1"
                        @click="deleteAnnotation(annotation.id)"
                      >
                        Delete
                      </VBtn>
                    </div>
                  </VCardText>
                </VCard>
              </VMenu>
            </div>
          </div>
        </div>
        <VAlert
          v-else
          show
          variant="tonal"
          color="warning"
          class="text-body-2 mt-2"
        >
          This file type could not be displayed, please, use download link
        </VAlert>
      </div>
      <div
        v-else
        style="margin: auto;"
      >
        <h4>File not provided</h4>
      </div>
    </VCol>
  </VRow>
</template>

<style lang="scss">
.artwork-file-pdf-container {
  overflow: scroll;
  max-width: 100%;
  // max-height: display size - header height - top and bottom dialog padding - artwork file pagination height
  max-height: calc(100vh - var(--header-height) - 40px - 22.5px);
  .artwork-file-pdf-wrapper {
    position: relative;
    margin: 0 auto;
    &:active {
      cursor: all-scroll;
    }
    .pdfAnnotation {
      position: absolute;
      display: block;
      width: 24px;
      height: 24px;
      background: #dd082b;
      border-radius: 50%;
      text-align: center;
      color: #fff;
      cursor: pointer;
      &.hovered {
        background: #fff;
        color: #dd082b;
        border: 1px solid #dd082b;
      }
    }
  }
}

canvas {
  width: 100% !important;
  height: unset !important;
}

.artwork-row {
  max-height: inherit;
  position: relative;
  > .v-col {
    max-height: inherit;
  }
}

.file-attachment-review-pdf-pagination-block {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(0, -50%);
}
</style>
